import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Mic, MicOff, Sparkles, Loader2, X as XIcon, RotateCcw, Zap } from 'lucide-react';

function detectSpeech() {
  if (typeof window === 'undefined') return { supported: false, Ctor: null };
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  return { supported: !!Ctor, Ctor };
}

function detectRecorder() {
  if (typeof window === 'undefined' || !window.MediaRecorder || !navigator?.mediaDevices?.getUserMedia) {
    return false;
  }
  return true;
}

/** BCP-47 defaults chosen to match Spanish (Puerto Rico) and US English. */
const LANG_OPTIONS = [
  { key: 'auto', label: 'Automático', locale: '' },
  { key: 'es', label: 'Español', locale: 'es-PR' },
  { key: 'en', label: 'English', locale: 'en-US' },
];

/** Quick heuristic: guess ES vs EN from text markers. */
function guessLangFromText(text) {
  if (!text) return '';
  const t = ' ' + text.toLowerCase() + ' ';
  const es = [' el ', ' la ', ' los ', ' las ', ' que ', ' de ', ' en ', ' y ', ' un ', ' una ', ' pago ', ' factura ', ' cliente ', ' por '];
  const en = [' the ', ' and ', ' of ', ' to ', ' a ', ' is ', ' for ', ' with ', ' client ', ' invoice ', ' pay '];
  const es_c = es.reduce((a, m) => a + (t.includes(m) ? 1 : 0), 0);
  const en_c = en.reduce((a, m) => a + (t.includes(m) ? 1 : 0), 0);
  if (es_c === en_c) return '';
  return es_c > en_c ? 'es' : 'en';
}

/**
 * DictationButton with multi-language detect + optional Whisper premium mode.
 *
 * Modes:
 *   - Web Speech API (free, real-time; needs Chrome/Edge/Safari)
 *   - Whisper (premium; needs firm OpenAI API key; better accuracy w/ noise)
 *
 * Props:
 *   onParsed(payload)  called with parsed invoice data
 *   compact            small icon-only variant
 *   defaultLang        'auto' | 'es' | 'en' — defaults from browser locale
 *   label              button label
 */
export function DictationButton({
  onParsed,
  compact = false,
  defaultLang = 'auto',
  label = 'Dictar factura',
  testId = 'open-dictation-btn',
}) {
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [langKey, setLangKey] = useState(() => localStorage.getItem('ezdockit.dict.lang') || defaultLang);
  const [whisperEnabled, setWhisperEnabled] = useState(false);
  const [aiCfg, setAiCfg] = useState(null);
  // Whisper mode state
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recRef = useRef(null);          // SpeechRecognition
  const mediaRecRef = useRef(null);     // MediaRecorder
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const shouldStopRef = useRef(false);
  const { supported, Ctor } = detectSpeech();
  const recorderSupported = detectRecorder();

  const activeLocale = (LANG_OPTIONS.find(l => l.key === langKey) || LANG_OPTIONS[0]).locale;
  const activeLang = langKey === 'auto' ? '' : langKey;

  // Load firm AI config (to know if Whisper is available)
  useEffect(() => {
    if (!open) return;
    api.get('/firm/ai-config').then(r => setAiCfg(r.data)).catch(() => setAiCfg(null));
  }, [open]);

  useEffect(() => {
    localStorage.setItem('ezdockit.dict.lang', langKey);
  }, [langKey]);

  // ---------- Web Speech API mode ----------
  const startListening = useCallback(() => {
    setError('');
    if (!supported) {
      setError('Su navegador no soporta reconocimiento de voz. Use Chrome, Edge o Safari.');
      return;
    }
    try {
      const rec = new Ctor();
      rec.lang = activeLocale || (navigator.language || 'es-PR');
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;
      rec.onresult = (event) => {
        let interimText = '';
        let finalText = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const chunk = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalText += chunk + ' ';
          else interimText += chunk;
        }
        if (finalText) setTranscript(prev => (prev + ' ' + finalText).trim());
        setInterim(interimText);
      };
      rec.onerror = (e) => {
        const msg = e?.error || 'unknown';
        if (msg === 'not-allowed' || msg === 'service-not-allowed') {
          setError('Permiso de micrófono denegado. Habilite el micrófono para este sitio.');
        } else if (msg !== 'no-speech' && msg !== 'aborted') {
          setError(`Error de dictado: ${msg}`);
        }
      };
      rec.onend = () => {
        if (!shouldStopRef.current) {
          try { rec.start(); return; } catch (_) { /* ignore */ }
        }
        setListening(false);
        setInterim('');
      };
      recRef.current = rec;
      shouldStopRef.current = false;
      rec.start();
      setListening(true);
    } catch (e) {
      setError(`No se pudo iniciar el dictado: ${e?.message || e}`);
    }
  }, [Ctor, supported, activeLocale]);

  const stopListening = useCallback(() => {
    shouldStopRef.current = true;
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
    setListening(false);
  }, []);

  // ---------- Whisper mode ----------
  const startRecording = useCallback(async () => {
    setError('');
    if (!recorderSupported) {
      setError('MediaRecorder no está disponible. Use Web Speech.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true } });
      streamRef.current = stream;
      chunksRef.current = [];
      // Prefer opus/webm for best compression + wide browser support.
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
                : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
                : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try {
          streamRef.current?.getTracks?.().forEach(t => t.stop());
        } catch (_) { /* ignore */ }
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (!blob.size) return;
        await uploadWhisper(blob, rec.mimeType || 'audio/webm');
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setError(`No se pudo acceder al micrófono: ${e?.message || e}`);
    }
  }, [recorderSupported]);

  const stopRecording = useCallback(() => {
    setRecording(false);
    try { mediaRecRef.current?.stop(); } catch (_) { /* ignore */ }
  }, []);

  const uploadWhisper = useCallback(async (blob, mime) => {
    setUploading(true);
    try {
      const form = new FormData();
      const ext = mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `dictation.${ext}`);
      if (activeLang) form.append('language', activeLang);
      const { data } = await api.post('/ai/transcribe-audio', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text = (data.transcript || '').trim();
      if (text) {
        setTranscript(prev => (prev ? prev + ' ' + text : text));
        // Auto language detect from Whisper output
        if (langKey === 'auto' && data.language) {
          // update UI hint (does not overwrite user's chosen langKey)
        }
        toast.success(`Whisper transcribió ${text.length} caracteres`);
      } else {
        toast.error('Whisper no devolvió texto');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Fallo Whisper. Revise la API key en Ajustes > IA/Voz.';
      setError(msg);
      toast.error(msg);
    } finally { setUploading(false); }
  }, [activeLang, langKey]);

  const reset = () => {
    stopListening();
    stopRecording();
    setTranscript(''); setInterim(''); setError('');
  };

  useEffect(() => {
    if (!open) { stopListening(); stopRecording(); }
  }, [open, stopListening, stopRecording]);

  // Auto-suggest ES/EN when auto + text present
  const autoDetectedLang = langKey === 'auto' ? guessLangFromText(transcript) : '';

  const send = async () => {
    const text = transcript.trim();
    if (!text) return toast.error('Grabe o escriba algo primero');
    stopListening();
    stopRecording();
    setParsing(true);
    try {
      const { data } = await api.post('/ai/parse-invoice-dictation', { transcript: text });
      toast.success('Factura interpretada por el asistente');
      onParsed?.(data);
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al procesar dictado';
      toast.error(msg); setError(msg);
    } finally { setParsing(false); }
  };

  const canWhisper = whisperEnabled && recorderSupported && aiCfg?.has_openai_api_key;
  const isBusy = listening || recording || uploading || parsing;

  return (
    <>
      <Button variant="outline" size={compact ? 'icon' : 'sm'} onClick={() => setOpen(true)} data-testid={testId} className={compact ? '' : 'gap-2'} title={label}>
        <Mic className="h-4 w-4" />
        {!compact && <span>{label}</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="dictation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-600" /> Dictar factura al asistente
            </DialogTitle>
            <DialogDescription>
              Presione <b>Grabar</b> y diga los datos en voz alta. Al terminar, presione <b>Enviar</b>.
              Ejemplo: <em>“Facturar a Juan Pérez dos horas de consulta a ciento cincuenta dólares. Pago en efectivo.”</em>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">Idioma:</span>
            <Select value={langKey} onValueChange={setLangKey}>
              <SelectTrigger className="h-7 w-[140px]" data-testid="dictation-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANG_OPTIONS.map(l => (
                  <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {autoDetectedLang && <Badge variant="outline" className="text-[10px]">Detecto: {autoDetectedLang.toUpperCase()}</Badge>}

            {aiCfg?.has_openai_api_key ? (
              <label className="ml-auto flex items-center gap-1.5 cursor-pointer" data-testid="whisper-toggle-label">
                <Zap className={`h-3.5 w-3.5 ${whisperEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span>Whisper</span>
                <Switch checked={whisperEnabled} onCheckedChange={setWhisperEnabled} data-testid="whisper-toggle" />
              </label>
            ) : (
              <span className="ml-auto text-[10px] text-slate-400" data-testid="whisper-not-configured">Whisper: no configurado</span>
            )}
          </div>

          <div className="rounded-lg border p-4 min-h-[140px] bg-slate-50 mt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {(listening || recording || uploading) ? (
                  <span className="relative flex items-center gap-2 text-rose-700 text-sm font-medium" data-testid="dictation-listening">
                    <span className="relative inline-flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-600" />
                    </span>
                    {uploading ? 'Transcribiendo con Whisper…' : recording ? 'Grabando (Whisper)…' : 'Escuchando…'}
                  </span>
                ) : (
                  <span className="text-sm text-slate-500 flex items-center gap-1.5"><MicOff className="h-3.5 w-3.5" /> Detenido</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {transcript && (
                  <Button size="icon" variant="ghost" onClick={reset} title="Borrar" data-testid="dictation-reset-btn">
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <Textarea
              value={transcript + (interim ? ' ' + interim : '')}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Aquí aparecerá lo que dicte… (o escriba directamente)"
              className="min-h-[100px] bg-white"
              data-testid="dictation-transcript"
            />
            {!supported && !canWhisper && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Su navegador no soporta reconocimiento nativo. Puede escribir el texto arriba o activar Whisper (requiere API key en Ajustes).
              </div>
            )}
            {error && (
              <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="dictation-error">
                {error}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              {canWhisper ? (
                !recording ? (
                  <Button onClick={startRecording} className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={isBusy} data-testid="dictation-whisper-record-btn">
                    <Zap className="h-4 w-4 mr-1" /> Grabar (Whisper)
                  </Button>
                ) : (
                  <Button onClick={stopRecording} variant="outline" data-testid="dictation-whisper-stop-btn">
                    <MicOff className="h-4 w-4 mr-1" /> Detener
                  </Button>
                )
              ) : supported && (
                !listening ? (
                  <Button onClick={startListening} className="bg-rose-600 hover:bg-rose-700 text-white" disabled={isBusy} data-testid="dictation-record-btn">
                    <Mic className="h-4 w-4 mr-1" /> Grabar
                  </Button>
                ) : (
                  <Button onClick={stopListening} variant="outline" data-testid="dictation-stop-btn">
                    <MicOff className="h-4 w-4 mr-1" /> Detener
                  </Button>
                )
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={parsing}>
                <XIcon className="h-4 w-4 mr-1" /> Cancelar
              </Button>
              <Button onClick={send} disabled={parsing || !transcript.trim()} data-testid="dictation-send-btn">
                {parsing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Enviar al asistente
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
